import AnimationComponent from '../../Component/Animation';
import Interactable from '../../Component/Interactable';
import CardComponent from '../../Component/Level/Card';
import TextComponent from '../../Component/Render/Text';
import Renderable from '../../Component/Renderable';
import TransformComponent from '../../Component/Transform';
import Game from '../../Game';
import GameObject from '../../GameObject/GameObject';
import Animator from '../../Prototype/Animator';
import Container from '../../Prototype/Container';
import BaseCard from '../../Prototype/Level/Card/BaseCard';
import { CardTypeMap } from '../../Prototype/Level/Card/CardData';
import Sprite from '../../Prototype/Sprite';
import Text from '../../Prototype/Text';
import { CardActions } from '../../State/Card/CardActions';
import { ICardsState } from '../../State/Card/CardReducer';
import CardStateHelper from '../../State/Card/CardStateHelper';
import { IGameState } from '../../State/Game/IGameState';
import { LevelStatus } from '../../State/Level/ILevelState';
import { getLevelStarsTotal } from '../../State/Player/PlayerLevelHelper';
import { getWorldStarsTotal } from '../../State/Player/PlayerStateHelper';
import Point from '../../Utility/Point';
import { delay } from '../../Utility/Task/TaskQueue';
import { StartAnimation, StopAnimation } from '../Animation/AnimationHelper';
import System from '../System';
import { Leaderboard } from '../UI/LeaderboardHelper';
import BonusCardsTask from './Task/BonusCardsTask';
import CardStateSyncTask from './Task/CardStateSyncTask';
import CreateNewCardsTask from './Task/CreateNewCardsTask';
import GoalMenuTask from './Task/GoalMenuTask';
import LevelCompleteMenuTask from './Task/LevelCompleteMenuTask';
import LevelCompleteTask from './Task/LevelCompleteTask';
import LevelIntroTask from './Task/LevelIntroTask';

export function getCardHandler(card: BaseCard) {
    const cardComponent = card.getComponent(CardComponent);
    return new CardTypeMap[cardComponent.type].handler(card);
}

export default class CardSystem extends System {
    public systemContainer: Container;
    public cardsContainer: Container;
    public stackOuterContainer: Container;
    public stackContainer: Container;
    public cardStackText: Text;
    public animator: Animator;
    public cards: BaseCard[];

    public currentLevelState: LevelStatus;

    private cardMatchHintRoutine: any;

    public onStart(): void {
        this.addContainers();
        this.cards = [];
    }

    public async preload(): Promise<any> {
        // Dragonbones Cards VFX.
        Game.resource.add(
            'CardsVFX_skeleton_data',
            'resource/dragonbones/card/skeleton.json'
        );
        Game.resource.add(
            'CardsVFX_texture',
            'resource/dragonbones/card/texture.png'
        );
        Game.resource.add(
            'CardsVFX_texture_data',
            'resource/dragonbones/card/texture.json'
        );

        Game.resource.add(
            'bonus_card_layouts',
            'resource/data/bonus_card_layouts.json'
        );

        await Game.resource.load();
    }

    private addContainers() {
        this.systemContainer = GameObject.Instantiate<Container>(Container, {
            name: 'cardSystemContainer',
        });
        this.cardsContainer = GameObject.Instantiate<Container>(Container, {
            name: 'cardsContainer',
            relativePosition: new Point(0.5, 0),
        });
        this.stackOuterContainer = GameObject.Instantiate<Container>(
            Container,
            {
                name: 'stackOuterContainer',
                relativePosition: new Point(0.5, 1),
                position: new Point(0, -95),
            }
        );
        this.stackContainer = GameObject.Instantiate<Container>(Container, {
            name: 'stackContainer',
        });

        const stackBackground = GameObject.Instantiate(Sprite, {
            texture: 'bottom_bar',
            anchor: new Point(0.5, 0.5),
            position: new Point(-75, 72),
            scale: new Point(1.1, 1.3),
        });

        this.stackOuterContainer.addChild(stackBackground);
        this.stackOuterContainer.addChild(this.stackContainer);

        // Create card stack text.
        this.cardStackText = GameObject.Instantiate<Text>(Text, {
            text: '999',
            position: new Point(-150, 45),
            anchor: new Point(0.5, 0.5),
            style: Game.style.getStyle('cardStack.text'),
        });
        this.cardStackText.addComponent(AnimationComponent);

        this.stackOuterContainer.addChild(this.cardStackText);

        this.animator = GameObject.Instantiate(Animator);

        this.systemContainer.addChild(this.cardsContainer);
        this.systemContainer.addChild(this.stackOuterContainer);
        this.systemContainer.addChild(this.animator);

        this.scene.addGameObject(this.systemContainer);
    }

    public handleStateChanged(state: IGameState) {
        // Always make sure we have the necessary game objects to draw the new state.
        this.taskQueue.queue(new CreateNewCardsTask());

        switch (state.levelState.status) {
            case LevelStatus.LOADING:
                this.currentLevelState = LevelStatus.LOADING;
                //
                break;
            case LevelStatus.INTRO:
                if (this.currentLevelState !== LevelStatus.INTRO) {
                    this.currentLevelState = LevelStatus.INTRO;
                    this.taskQueue.queue(new LevelIntroTask());
                    this.taskQueue.queue(new GoalMenuTask());
                    this.taskQueue.start(this);
                }

                break;
            case LevelStatus.ACTIVE:
                this.currentLevelState = LevelStatus.ACTIVE;

                this.taskQueue.queue(new CardStateSyncTask());
                this.taskQueue.start(this);

                // Restart hint timer coroutine.
                this.stopCoroutine(this.cardMatchHintRoutine);
                this.cardMatchHintRoutine = this.startCoroutine(
                    this.cardMatchHint(state)
                );
                break;
            case LevelStatus.COMPLETE:
                if (this.currentLevelState !== LevelStatus.COMPLETE) {
                    this.currentLevelState = LevelStatus.COMPLETE;

                    this.stopCoroutine(this.cardMatchHintRoutine);

                    // Sync last task.
                    // Resolve all card tasks before play complete sequence.
                    this.taskQueue.queue(new CardStateSyncTask());
                    this.taskQueue.start(this);

                    // Level completed sequence.
                    this.startCoroutine(this.levelCompleteSequence());

                    // Post scores. All leaderboards take the highest reported value.

                    // Score earned for completing this level.
                    Game.sdk.postScore(
                        Leaderboard.LEVEL_SCORE,
                        state.levelState.levelId,
                        state.levelState.score
                    );

                    // Number of stars earned in the current world.
                    Game.sdk.postScore(
                        Leaderboard.WORLD_STARS,
                        state.worldState.worldNumber.toString(),
                        getWorldStarsTotal(
                            state.playerState,
                            state.worldState.worldNumber
                        )
                    );

                    // Number of total stars earned.
                    Game.sdk.postScore(
                        Leaderboard.TOTAL_STARS,
                        'default',
                        getLevelStarsTotal(state.playerState)
                    );

                    // The total levels completed.
                    Game.sdk.postScore(
                        Leaderboard.TOTAL_LEVELS,
                        'default',
                        state.levelState.levelNumber
                    );
                }
                break;
        }
    }

    public playCard(cardID: string): void {
        this.state.dispatch(CardActions.playCard(cardID));
    }

    private *cardMatchHint(state: IGameState): IterableIterator<number> {
        while (true) {
            yield* delay(5000);

            const cardsFolded = this.getCardsFolded();
            const topCardFolded = cardsFolded[cardsFolded.length - 1];
            if (!topCardFolded || topCardFolded.destroyed) return;

            const matches = CardStateHelper.getCardMatches(state);
            if (matches.length > 0) {
                StartAnimation(this.getCardByID(matches[0]), 'CardPulse');
            } else {
                const cardsInStack = this.getCardsInStack();
                const topCardInStack = cardsInStack[cardsInStack.length - 1];
                if (!topCardInStack || topCardInStack.destroyed) return;

                StartAnimation(topCardInStack, 'CardPulse');
            }
        }
    }

    private *levelCompleteSequence(): IterableIterator<number> {
        while (this.taskQueue.isRunning()) yield;

        // Level completed sequence.
        this.taskQueue.queue(new BonusCardsTask());
        this.taskQueue.queue(new LevelCompleteTask());

        // call 'levelFinish' to show an ad
        this.taskQueue.queue({
            *execute() {
                let isComplete = false;
                Game.sdk.levelFinish('win').then(() => {
                    isComplete = true;
                });
                while (!isComplete) yield;
            },
        });

        // Show level complete menu
        this.taskQueue.queue(new LevelCompleteMenuTask());
        this.taskQueue.start(this);
    }

    public sortCardsInPlay(state: ICardsState) {
        state.inPlay
            .map(cardID => state.cards[cardID])
            .sort((a, b) => a.depth - b.depth)
            .forEach(cardID => {
                this.cardsContainer.addChild(this.getCardByID(cardID.id));
            });
    }

    public setCardStackText(value: number): void {
        if (!this.cardStackText) return;
        const textComponent = this.cardStackText.getComponent(TextComponent);
        textComponent.text = String(value);
        textComponent.visible = value > 0;
    }

    public getAllCards(): BaseCard[] {
        return [
            ...this.state.getState().levelState.cardsState.inPlay,
            ...this.state.getState().levelState.cardsState.discarded,
            ...this.state.getState().levelState.cardsState.inStack,
            ...this.state.getState().levelState.cardsState.folded,
        ].map((cardID: string) => {
            return this.getCardByID(cardID);
        }) as BaseCard[];
    }

    public getCardsInPlay(): BaseCard[] {
        const cardsInPlay = this.state.getState().levelState.cardsState.inPlay;
        return cardsInPlay.map((cardID: string) => {
            return this.getCardByID(cardID);
        }) as BaseCard[];
    }

    public getCardsInStack(): BaseCard[] {
        const cardsInStack = this.state.getState().levelState.cardsState
            .inStack;
        return cardsInStack.map((cardID: string) => {
            return this.getCardByID(cardID);
        }) as BaseCard[];
    }

    public getCardsFolded(): BaseCard[] {
        const cardsFolded = this.state.getState().levelState.cardsState.folded;
        return cardsFolded.map((cardID: string) => {
            return this.getCardByID(cardID);
        }) as BaseCard[];
    }

    public getCardByID(cardID: string): BaseCard {
        return this.cards.find(gameObject => gameObject.id === cardID);
    }

    private cardIsPosition(card: BaseCard, x: number, y: number): boolean {
        if (!card) return false;
        const transform = card.getComponent(TransformComponent);
        return (
            transform.position.x === x &&
            transform.position.y === y &&
            //transform.rotation === 0 &&
            card.parent === this.stackContainer
        );
    }

    public onLateUpdate(): void {
        if (
            (this.currentLevelState === LevelStatus.INTRO ||
                this.currentLevelState === LevelStatus.ACTIVE ||
                this.currentLevelState === LevelStatus.COMPLETE) &&
            this.cardStackText.parent === this.stackOuterContainer
        ) {
            this.updateStackCardVisibility();
            this.updateCardStackText();
        }
    }

    public updateCardStackText() {
        let value = 0;
        const cardsInStack = this.getCardsInStack();
        for (let i = 0, n = cardsInStack.length; i < n; i++) {
            if (this.cardIsPosition(cardsInStack[i], -150, 0)) {
                value++;
            }
        }

        const currentValue = parseInt(
            this.cardStackText.getComponent(TextComponent).text
        );

        // Stack text pulsate for low values.
        if (currentValue !== value) {
            if (value <= 5) {
                StartAnimation(this.cardStackText, 'PulseLoop', {
                    duration: 150,
                    scale1: new Point(1, 1),
                    scale2: new Point(1.2, 1.2),
                });
            } else if (value <= 10) {
                StartAnimation(this.cardStackText, 'PulseLoop', {
                    duration: 300,
                    scale1: new Point(1, 1),
                    scale2: new Point(1.2, 1.2),
                });
            } else {
                this.cardStackText
                    .getComponent(TransformComponent)
                    .scale.set(1, 1);
                StopAnimation(this.cardStackText);
            }
        }

        this.setCardStackText(value);
    }

    public updateStackCardVisibility() {
        const cardsInStack = this.getCardsInStack();

        for (let i = 0, n = cardsInStack.length; i < n; i++) {
            if (this.cardIsPosition(cardsInStack[i], -150, 0)) {
                if (this.cardIsPosition(cardsInStack[i + 1], -150, 0)) {
                    cardsInStack[i].getComponent(Renderable).visible = false;
                } else {
                    cardsInStack[i].getComponent(Renderable).visible = true;
                    cardsInStack[i].getComponent(Interactable).enabled = true;
                }
            }
        }

        const cardsFolded = this.getCardsFolded();

        for (let i = 0, n = cardsFolded.length; i < n; i++) {
            if (this.cardIsPosition(cardsFolded[i], 0, 0)) {
                if (this.cardIsPosition(cardsFolded[i + 1], 0, 0)) {
                    cardsFolded[i].getComponent(Renderable).visible = false;
                } else {
                    cardsFolded[i].getComponent(Renderable).visible = true;
                }
            }
        }
    }

    public init(): void {}

    protected onDestroy(): void {}

    protected onStop(): void {}

    protected onUpdate(): void {}
}
